import { useState } from "react";
import { Landmark } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const VIOLET = "#6D5DF6";
const FR_MONTHS = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet",
  "Août", "Septembre", "Octobre", "Novembre", "Décembre"];

export default function CaYearModal({ year, existing, onClose, onDone }) {
  const [vals, setVals] = useState(() => {
    const init = {};
    FR_MONTHS.forEach((_, i) => {
      const key = `${year}-${String(i + 1).padStart(2, "0")}`;
      const found = (existing || []).find((m) => m.month === key);
      init[key] = found ? String(found.amount) : "";
    });
    return init;
  });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const entries = Object.entries(vals).filter(([, v]) => +v > 0);
      for (const [month, amount] of entries) {
        await api.post("/pilotage/income-manual", { month, amount: +amount });
      }
      toast.success(entries.length ? `CA enregistré pour ${entries.length} mois` : "Aucun montant saisi");
      onDone();
      onClose();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="ca-year-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-base flex items-center gap-2">
            <Landmark className="w-4 h-4" style={{ color: VIOLET }} /> CA encaissé — année {year}
          </DialogTitle>
        </DialogHeader>
        <p className="text-[11px] text-neutral-500">
          Saisissez le CA encaissé de chaque mois. Pour chaque mois, le montant le plus élevé entre
          les factures enregistrées et cette saisie est retenu — elle couvre les factures non
          saisies dans l'app, sans double comptage.
        </p>
        <div className="grid grid-cols-3 gap-2.5" data-testid="ca-year-grid">
          {FR_MONTHS.map((label, i) => {
            const key = `${year}-${String(i + 1).padStart(2, "0")}`;
            return (
              <label key={key} className="text-[11px] text-neutral-500">
                {label}
                <Input type="number" min="0" step="0.01" placeholder="0,00 €" value={vals[key]}
                  onChange={(e) => setVals({ ...vals, [key]: e.target.value })}
                  className="h-8 text-xs mt-1" data-testid={`ca-month-${key}`} />
              </label>
            );
          })}
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} className="h-8 text-xs" data-testid="ca-year-cancel">Annuler</Button>
          <Button onClick={save} disabled={busy} className="h-8 text-xs text-white" style={{ background: VIOLET }} data-testid="ca-year-save">
            {busy ? "Enregistrement…" : "Enregistrer"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
